<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Licenses",
};
</script>

<script setup lang="ts">
import LicensesTable from "@/components/models/license/LicenseTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import LicenseService, { type TLicense } from "@/api/license";
import DeviceService from "@/api/device";
import ServiceService from "@/api/service";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import type { PaginatedType, VFormFieldChoice } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Licenses",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.LICENSE);
    },
  },
});

const snackStore = useSnackStore();
const licenseFetchEnabled = ref(false);

const deviceChoices = ref<VFormFieldChoice<any>[]>([]);
const serviceChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  licenses: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: licenses,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["licenses"],
  queryFn: async (): Promise<PaginatedType<TLicense[]> | undefined> => {
    try {
      const [licenses, devicesSelectable, servicesSelectable] = await Promise.all([
        LicenseService.license.getAll(queriesTableParams.licenses),
        DeviceService.device.getAllSelectable(),
        ServiceService.service.getAllSelectable({ ...DefaultServerTableParams, itemsPerPage: 100 }),
      ]);

      deviceChoices.value = devicesSelectable;
      serviceChoices.value = servicesSelectable;

      return licenses.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => licenseFetchEnabled.value,
});

onMounted(async () => {
  licenseFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <licenses-table
        :query-key="'licenses'"
        :data="licenses"
        :devices="deviceChoices"
        :services="serviceChoices"
        :loading="isPending || isFetching"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
